import { AnalyticsTrackingEvent, isProductEligibleForCheckout, ProductRewardSearchResult } from '@brandclub/common-ui';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useDomainConfig } from '../../api/cache/appConfigQueryOptions';
import { brandclubProductQueryOptions } from '../../api/cache/dtcProductQueryOptions';
import { useTrackActions } from '../../hooks/useTracking';
import { useBCRouting } from '../../providers/BCRoutingContextProvider';
import BagDrawer from '../BagButton/BagDrawer';
import { getPriorityEligibleProduct } from './inpageAddToBagHelpers';
import { ADD_NOT_FINISHED, AddToBagJob, AddToBagOutcome, useAddToBagIntent } from './useAddToBagIntent';
import { useAddToBagWrite } from './useAddToBagWrite';

/**
 * Runs the adds a host asks for (`window.BCCheckoutBar.addToBag`), as the in-page button does: it checks that the
 * product can be checked out, then adds it through `useAddToBagWrite`, or opens the retailer's page. Each add runs
 * to its end and answers its host. A refusal shows in the bag drawer, so the host does not show it again.
 * Renders nothing.
 */
const AddToBagController: React.FC = () => {
  const requestId = useAddToBagIntent((s) => s.requestId);

  const queryClient = useQueryClient();
  const { data: domainConfig } = useDomainConfig();
  const addToBag = useAddToBagWrite();
  const [trackCheckoutBarAction] = useTrackActions();
  const bcRouting = useBCRouting();

  const { treatments } = useSplitTreatments({
    names: ['brandclub_checkout_disabled_domain'],
    attributes: {
      domain: typeof window !== 'undefined' ? window.location.hostname : '',
      bcSplit: typeof window !== 'undefined' ? (window as any).bcSplit : undefined,
    },
  });
  const disableCheckoutByDomain = treatments?.brandclub_checkout_disabled_domain?.treatment === 'disable';

  /** An add that never reached the cart says why in the drawer, as a refused save does. */
  const notAdded = (): AddToBagOutcome => {
    BagDrawer.refuse(ADD_NOT_FINISHED);
    return { status: 'refused', message: ADD_NOT_FINISHED };
  };

  const run = async ({ stacklineSku, retailerId, quantity = 1 }: AddToBagJob): Promise<AddToBagOutcome> => {
    const root = (await queryClient.ensureQueryData(brandclubProductQueryOptions(stacklineSku))) as
      | ProductRewardSearchResult
      | undefined;
    if (!root) return notAdded();

    let target: ProductRewardSearchResult | undefined;
    if (typeof retailerId === 'number') {
      const candidates: ProductRewardSearchResult[] = [
        root,
        ...((root.matchedProducts || []) as ProductRewardSearchResult[]),
      ];
      target = candidates.find((p) => p.retailerId === retailerId);
    }
    if (!target) {
      target = getPriorityEligibleProduct(root, domainConfig?.checkoutAllowedOfferType);
    }
    if (!target) return notAdded();

    const eligible =
      isProductEligibleForCheckout(target, domainConfig?.checkoutAllowedOfferType) && !disableCheckoutByDomain;
    if (eligible) return addToBag(target, quantity);

    trackCheckoutBarAction(AnalyticsTrackingEvent.OPEN_RETAILER_PRODUCT_PAGE, {
      stacklineSku: target.stacklineSku,
      retailerId: target.retailerId,
      retailerSku: target.retailerSku,
      retailPrice: target.retailPrice,
      source: 'brandclub_dtc_checkout_bar',
    });
    if (!target.retailerProductPageUrl) return notAdded();
    bcRouting.navigate(target.retailerProductPageUrl, '_blank');
    return { status: 'retailerPage' };
  };

  useEffect(() => {
    if (!requestId) return;
    // Take the jobs here, so a second run of this effect finds none and no add runs twice.
    for (const job of useAddToBagIntent.getState().take()) {
      run(job).then(job.settle, (error: unknown) => {
        console.error('AddToBagController failed', error);
        job.settle(notAdded());
      });
    }
    // eslint-disable-next-line -- exhaustive-deps intentionally relaxed (requestId drives the effect)
  }, [requestId]);

  return null;
};

export default AddToBagController;
